/**
 * @module lib/commercial/tier-ads
 * @description Certain PB features within inline flex ads need additional processing of ad slots,
 * based on the viewport tier, before the creation of the ads happens.
 * We use various `js-f-X` classes to filter out undesired ads, and update their
 * `id` and `target` attribute values accordingly.
 */
import tgamConfig from "config/tgam";
import { isMobile } from "utils/detect";

/**
 * @method filterTierAdverts
 * @description Filter certain Ads based on the viewport tier
 * @param {Array} pgUnits - of ad slot units
 * @returns {Array} of filtered ad slot units
 */
export function filterTierAdverts(pgUnits) {
  // Exclude financial pages, they do contain `global/story-feed` features, but aren't long enough to display an Ad.
  // Include curated/automated section pages, which have `global/story-feed` feature (with inline ads).
  // Include story article pages, which have `article/content` feature (with inline ads).
  if (tgamConfig.meta.isFinancialPage || (!tgamConfig.meta.isTemplatePbLayout && !tgamConfig.meta.isStoryPage)) {
    return pgUnits;
  }
  const keyClass = "js-f-ad"; // only process these
  const filterClass = "js-f-ad-" + (isMobile() ? "xs" : "sm"); // mobile (xs) or non-mobile (sm) [tablet to dekstop]

  // remove Ads that should not be displayed on this tier
  const units = pgUnits.filter(ad => {
    const hasKey = ad.classList.contains(keyClass);
    const hasClass = ad.classList.contains(filterClass);
    const result = !hasKey || (hasKey && hasClass);
    if (!result) {
      // pageUnit will be removed, but since we are also renaming element IDs, we should remove from the DOM too
      const parent = ad.closest(".j-f-wrap");
      parent.parentNode.removeChild(parent);
    }
    return result;
  });
  reApplyAdIds(units);
  return units;
}

/**
 * @method reApplyAdIds
 * @description Re-apply ad element IDs in sequential order by type
 * (function mutates the elements in-place instead of returning a new array)
 * @param {Array} pgUnits - of ad slot units to be mutated
 */
export function reApplyAdIds(pgUnits) {
  let counts = {};
  pgUnits.forEach(ad => {
    if (ad.classList.contains("js-f-ad")) { // only process these
      // this is similar to how the backend commercial Ad feature applies Id values
      const cnt = counts[ad.dataset.type] ? counts[ad.dataset.type] : 1;
      ad.id = ad.dataset.type + "-gpt-" + cnt;
      ad.dataset.target = JSON.stringify({ pos: "flex" + ((cnt > 1) ? cnt : ""), ptf: "gpt" });
      ad.parentNode.parentNode.id = "c-ad--" + ad.id;
      counts[ad.dataset.type] = cnt + 1;
    }
  });
}
